import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import OrderApi from '../../store/order/OrderApi';
import { IOrderRevisionLogEntry } from '../../store/order/orderStore';
import OrderActivityPanel from './OrderActivityPanel';
import { closeOrderHistoryPage } from '../../utils/lastShopReturnContext';

export interface CustomerOrderSummaryItem {
    appOrderId: number;
    orderType?: string;
    orderState?: number;
    externalDocumentNo?: string;
    navImportRef?: string;
    salespersonNote?: string;
    lastActivityAt?: string;
    activityCount?: number;
    createdUtc?: string;
}

type OrderStateTab = 'open' | 'closed';

const PAGE_SIZE = 25;

const OrderHistoryPage: React.FC = () => {
    const history = useHistory();
    const [activeTab, setActiveTab] = useState<OrderStateTab>('open');
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [skip, setSkip] = useState(0);
    const [items, setItems] = useState<CustomerOrderSummaryItem[]>([]);
    const [totalCount, setTotalCount] = useState(0);
    const [listLoading, setListLoading] = useState(false);
    const [selectedOrderId, setSelectedOrderId] = useState<number | null>(null);
    const [entries, setEntries] = useState<IOrderRevisionLogEntry[]>([]);
    const [detailLoading, setDetailLoading] = useState(false);

    useEffect(() => {
        const timer = window.setTimeout(() => {
            setDebouncedSearch(search.trim());
            setSkip(0);
        }, 300);

        return () => window.clearTimeout(timer);
    }, [search]);

    useEffect(() => {
        let cancelled = false;

        const loadSummary = async () => {
            setListLoading(true);

            try {
                const response = await OrderApi.getCustomerOrderActivitySummary(
                    skip,
                    PAGE_SIZE,
                    activeTab,
                    debouncedSearch || undefined
                );
                if (cancelled) {
                    return;
                }

                const data = response.data || {};
                const nextItems: CustomerOrderSummaryItem[] = data.items || [];
                setItems(nextItems);
                setTotalCount(data.totalCount || 0);

                if (nextItems.length === 0) {
                    setSelectedOrderId(null);
                    setEntries([]);
                    return;
                }

                if (!selectedOrderId || !nextItems.some((item) => item.appOrderId === selectedOrderId)) {
                    setSelectedOrderId(nextItems[0].appOrderId);
                }
            } catch (e) {
                if (!cancelled) {
                    setItems([]);
                    setTotalCount(0);
                    setSelectedOrderId(null);
                    setEntries([]);
                }
            } finally {
                if (!cancelled) {
                    setListLoading(false);
                }
            }
        };

        loadSummary();

        return () => {
            cancelled = true;
        };
    }, [activeTab, debouncedSearch, skip]);

    useEffect(() => {
        if (!selectedOrderId) {
            setEntries([]);
            return;
        }

        let cancelled = false;

        const loadDetail = async () => {
            setDetailLoading(true);

            try {
                const response = await OrderApi.getOrderRevisionLog(selectedOrderId, undefined, 50);
                if (cancelled) {
                    return;
                }

                setEntries((response.data && response.data.entries) || []);
            } catch (e) {
                if (!cancelled) {
                    setEntries([]);
                }
            } finally {
                if (!cancelled) {
                    setDetailLoading(false);
                }
            }
        };

        loadDetail();

        return () => {
            cancelled = true;
        };
    }, [selectedOrderId]);

    const selectedItem = items.find((item) => item.appOrderId === selectedOrderId) || null;
    const pageCount = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
    const currentPage = Math.floor(skip / PAGE_SIZE) + 1;

    const formatOrderLabel = (item: CustomerOrderSummaryItem) => {
        const parts = [`B2B${item.appOrderId}`];
        if (item.navImportRef) {
            parts.push(item.navImportRef);
        }
        return parts.join(' · ');
    };

    const formatActivityDate = (value?: string) => {
        if (!value) {
            return 'No activity logged';
        }

        return new Date(value).toLocaleString();
    };

    useEffect(() => {
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key !== 'Escape') {
                return;
            }

            event.preventDefault();
            closeOrderHistoryPage(history);
        };

        document.addEventListener('keydown', onKeyDown);

        return () => {
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [history]);

    return (
        <div className="order-history-page">
            <div className="order-history-page__header">
                <div>
                    <h1 className="order-history-page__title">Activity history</h1>
                    <p className="order-history-page__subtitle">
                        Browse open and completed orders and see who changed what.
                    </p>
                </div>
            </div>

            <div className="order-history-page__layout">
                <aside className="order-history-page__list">
                    <div className="order-history-page__tabs">
                        <button
                            type="button"
                            className={`order-history-page__tab${activeTab === 'open' ? ' order-history-page__tab--active' : ''}`}
                            onClick={() => {
                                setActiveTab('open');
                                setSkip(0);
                            }}
                        >
                            Open
                        </button>
                        <button
                            type="button"
                            className={`order-history-page__tab${activeTab === 'closed' ? ' order-history-page__tab--active' : ''}`}
                            onClick={() => {
                                setActiveTab('closed');
                                setSkip(0);
                            }}
                        >
                            Completed
                        </button>
                    </div>

                    <input
                        className="order-history-page__search"
                        type="search"
                        placeholder="Search order id, type, ref…"
                        value={search}
                        onChange={(event) => setSearch(event.target.value)}
                    />

                    {listLoading && <p className="order-history-page__status">Loading orders…</p>}

                    {!listLoading && items.length === 0 && (
                        <p className="order-history-page__status">No orders found.</p>
                    )}

                    <ul className="order-history-page__orders">
                        {items.map((item) => (
                            <li key={item.appOrderId}>
                                <button
                                    type="button"
                                    className={`order-history-page__order${selectedOrderId === item.appOrderId ? ' order-history-page__order--active' : ''}`}
                                    onClick={() => setSelectedOrderId(item.appOrderId)}
                                >
                                    <span className="order-history-page__order-id">{formatOrderLabel(item)}</span>
                                    <span className="order-history-page__order-type">{item.orderType || '—'}</span>
                                    <span className="order-history-page__order-meta">
                                        {item.activityCount || 0} changes · {formatActivityDate(item.lastActivityAt)}
                                    </span>
                                </button>
                            </li>
                        ))}
                    </ul>

                    {totalCount > PAGE_SIZE && (
                        <div className="order-history-page__pagination">
                            <button
                                type="button"
                                disabled={skip <= 0}
                                onClick={() => setSkip(Math.max(0, skip - PAGE_SIZE))}
                            >
                                Previous
                            </button>
                            <span>{currentPage} / {pageCount}</span>
                            <button
                                type="button"
                                disabled={skip + PAGE_SIZE >= totalCount}
                                onClick={() => setSkip(skip + PAGE_SIZE)}
                            >
                                Next
                            </button>
                        </div>
                    )}
                </aside>

                <section className="order-history-page__detail">
                    {!selectedItem && (
                        <p className="order-history-page__status">Select an order to view its history.</p>
                    )}

                    {selectedItem && (
                        <>
                            <div className="order-history-page__detail-header">
                                <div>
                                    <h2>{formatOrderLabel(selectedItem)}</h2>
                                    <p>
                                        {selectedItem.orderType || '—'}
                                        {selectedItem.salespersonNote ? ` · ${selectedItem.salespersonNote}` : ''}
                                    </p>
                                </div>
                                <button
                                    type="button"
                                    className="order-history-page__go"
                                    onClick={() => history.push(`/dashboard/${selectedItem.appOrderId}`)}
                                >
                                    Go to order
                                </button>
                            </div>

                            <OrderActivityPanel
                                entries={entries}
                                isLoading={detailLoading}
                                showContributors={true}
                                enableContributorFilter={true}
                                showProductThumbnails={true}
                                lineNavigationMode="button"
                                lineNavigationLabel="Go to line in order"
                                onLineClick={(lineId) => {
                                    if (!lineId) {
                                        return;
                                    }
                                    history.push(`/dashboard/${selectedItem.appOrderId}?step=details`);
                                }}
                            />
                        </>
                    )}
                </section>
            </div>
        </div>
    );
};

export default OrderHistoryPage;
